import { ResolverTag } from '@ps/web-core-enums';

import store, { select } from '@swf/core/store';
import { clearAll } from '@swf/core/utils/auth';

export enum ConnectionType {
  WMT,
  Rewards
}

export interface APIConfig {
  requestDestination: string;
  getHeaders?(): object | undefined;
  responseChannel: string;
  requestTimeout: number;
  sockJSOptions?: object;
  type: ConnectionType;
  url: string;
  getReconnect(currentDelay: number, disconnectReason: string): number;
}

export enum WMTDisconnectReason {
  BadFormat = 'BF',
  InvalidSubscriptionDestination = 'ID',
  UnknownOrigin = 'UO',
  InvalidMessageType = 'MT',
  TokenRequired = 'TR',
  UserChangeNotAllowed = 'UC',
  BadSignature = 'BS',
  InvalidToken = 'IT',
  InvalidSessionId = 'IS',
  VelocityControl = 'VC',
  MaxSubscriptionExceeded = 'MS'
}

const WMT_RECONNECT = 1000;

export const WMTConfig = (): APIConfig => {
  const { getState } = store;
  const { requestTimeout } = select.config.get(getState());

  return {
    requestDestination: '/service/',
    requestTimeout: requestTimeout ? requestTimeout : 20000,
    responseChannel: '/user/queue/messages',
    type: ConnectionType.WMT,
    url: '/api/v0/websocket',
    sockJSOptions: { transports: ['websocket'] },
    getHeaders: () => {
      const { token } = select.session.get(getState());
      return token ? { authorization: `StarsWeb-Session ${token}` } : undefined;
    },
    getReconnect: (currentDelay, reason: WMTDisconnectReason) => {
      if (reason === WMTDisconnectReason.InvalidToken) {
        clearAll();
      }
      if (!currentDelay) {
        return reason === WMTDisconnectReason.VelocityControl ? WMT_RECONNECT : 1;
      }
      return currentDelay === 1 ? WMT_RECONNECT : currentDelay * 2;
    }
  };
};

const rewardsConfig = async (): Promise<APIConfig> => {
  const { dispatch, getState } = store;
  await dispatch.resolver.fetchTag({ tag: ResolverTag.StarsRewardsWidgetService });
  const { url } = select.resolver.getTag(getState(), ResolverTag.StarsRewardsWidgetService);

  return {
    // @TODO infer reconnect from disconnect reason
    getReconnect: () => 5000,
    getHeaders: () => {
      const { token, signature, userWebId } = select.session.getLegacySession(getState());
      return { WBID: userWebId, SBTK: token, SBSG: signature };
    },
    requestDestination: '/app/starsrewards/api',
    requestTimeout: 10000,
    responseChannel: `/topic/api/${select.session.getLegacySession(getState()).token}`,
    type: ConnectionType.Rewards,
    url
  };
};

export const getConfig = async (type: ConnectionType) => {
  switch (type) {
    case ConnectionType.Rewards:
      return rewardsConfig();
    case ConnectionType.WMT:
      return WMTConfig();
  }
};
